import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const AssignContractModal = ({
  open,
  onClose,
  parentContract,
  allContracts = [],
  allRelations = [],
  onSave,
  onDeleteChild,
  existingChildren = [],
  loadingChildren = false,
}) => {
  const [selectedNew, setSelectedNew] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (open) setSelectedNew([]);
  }, [open, parentContract?.contractUniqueID]);

  const parentId = parentContract?.contractUniqueID;

  // ── Compute exclusion sets from allRelations ──────────────────────────────
  const { alreadyChildSPs, alreadyParentSPs, existingChildSPs } = useMemo(() => {
    // Every contractUniqueID that appears as a childSP anywhere → already someone's child
    const alreadyChildSPs = new Set(allRelations.map(r => r.childSP));

    // Every contractUniqueID that appears as a parentSP anywhere → already a parent
    const alreadyParentSPs = new Set(allRelations.map(r => r.parentSP));

    // childSPs already assigned specifically to THIS parent (shown in table, not dropdown)
    const existingChildSPs = new Set(existingChildren.map(c => c.childSP));

    return { alreadyChildSPs, alreadyParentSPs, existingChildSPs };
  }, [allRelations, existingChildren]);

  // ── Build dropdown options: only truly free contracts ─────────────────────
  // Excluded if: is self, already a child anywhere, already a parent, or already child of this parent
  const autocompleteOptions = useMemo(
    () =>
      allContracts.filter(c => {
        const id = c.contractUniqueID;
        if (id === parentId) return false; // self
        if (existingChildSPs.has(id)) return false; // already child of THIS parent (in table)
        if (alreadyChildSPs.has(id)) return false; // already child of another parent
        if (alreadyParentSPs.has(id)) return false; // already a parent
        return true;
      }),
    [allContracts, parentId, existingChildSPs, alreadyChildSPs, alreadyParentSPs],
  );

  // ── Save / delete handlers ────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedNew.length) return;
    setSaving(true);
    try {
      await onSave(parentId, selectedNew);
      setSelectedNew([]);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChild = async relationId => {
    setDeletingId(relationId);
    try {
      await onDeleteChild(parentId, relationId);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography fontWeight={600} fontSize={16}>
          Assign child contracts
        </Typography>
        {parentContract && (
          <Typography fontSize={13} color="text.secondary" mt={0.5}>
            Parent:&nbsp;<strong>{parentId}</strong>
            {parentContract.wH_NumberName ? ` · ${parentContract.wH_NumberName}` : ''}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography
          fontSize={11}
          fontWeight={600}
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing={0.5}
          mb={0.75}
          mt={2}>
          Add child contracts
        </Typography>

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={autocompleteOptions}
          value={selectedNew}
          onChange={(_, val) => setSelectedNew(val)}
          getOptionLabel={opt => opt.contractUniqueID}
          isOptionEqualToValue={(opt, val) => opt.contractUniqueID === val.contractUniqueID}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return options.filter(
              o =>
                o.contractUniqueID?.toLowerCase().includes(q) ||
                o.wH_NumberName?.toLowerCase().includes(q) ||
                o.customerName?.toLowerCase().includes(q),
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((opt, index) => (
              <Chip
                key={opt.contractUniqueID}
                label={opt.contractUniqueID}
                size="small"
                color="primary"
                variant="outlined"
                {...getTagProps({ index })}
              />
            ))
          }
          renderOption={(props, option) => (
            <li {...props} key={option.contractUniqueID}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%' }}>
                <Typography fontSize={13} fontWeight={500} sx={{ minWidth: 100 }}>
                  {option.contractUniqueID}
                </Typography>
                <Typography fontSize={12} color="text.secondary" noWrap sx={{ flex: 1 }}>
                  {option.wH_NumberName || option.customerName || ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={selectedNew.length ? '' : 'Search by contract ID, site or customer…'}
              size="small"
              sx={{ mb: 2.5 }}
            />
          )}
        />

        {/* ── Already assigned children table ───────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography fontSize={11} fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
            Already assigned children
          </Typography>
          <Chip label={existingChildren.length} size="small" sx={{ height: 20, fontSize: 11 }} />
        </Box>

        {loadingChildren ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : existingChildren.length === 0 ? (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, py: 3, textAlign: 'center' }}>
            <Typography fontSize={13} color="text.disabled">
              No child contracts assigned yet
            </Typography>
          </Box>
        ) : (
          <Table
            size="small"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              '& td, & th': { borderColor: 'divider' },
            }}>
            <TableHead>
              <TableRow sx={{ background: 'action.hover' }}>
                <TableCell
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    color: 'text.secondary',
                  }}>
                  Contract ID
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    color: 'text.secondary',
                  }}>
                  Site / Details
                </TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {existingChildren.map(child => (
                <TableRow key={child.relationId} hover>
                  <TableCell>
                    <Typography fontSize={13} fontWeight={500} color="primary">
                      {child.childSP}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13} color="text.secondary">
                      {child.details || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deletingId === child.relationId}
                      onClick={() => handleDeleteChild(child.relationId)}
                      title="Remove relation">
                      {deletingId === child.relationId ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!selectedNew.length || saving}
          onClick={handleSave}
          sx={{ textTransform: 'none', minWidth: 110 }}>
          {saving ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            `Save${selectedNew.length ? ` (${selectedNew.length})` : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignContractModal;
